import {Component, ComponentRef, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {ActivatedRoute, Params, Router} from '@angular/router';

import {TranslateService} from '@ngx-translate/core';

import {EMPTY, merge, Observable, of} from 'rxjs';
import {catchError, debounceTime, filter, finalize, map, share, switchMap, takeUntil, tap} from 'rxjs/operators';

import {ToastrService} from 'ngx-toastr';
import {Destroyable} from '../../../../destroyable.component';
import {RolesService} from '../../../../shared/authentication/roles/roles.service';
import {SelectEmployee} from '../../../../shared/employees/employees.model';
import {LocalizationService} from '../../../../shared/localization/localization.service';
import {ModalService} from '../../../../shared/modal';
import {DefaultPagination, Pagination} from '../../../../shared/ui';
import {buildFiltersQuery, buildQueryParams, filtersAreDifferent} from '../../../../shared/utils/objects';
import {
  ReassignRequestPopupComponent
} from '../../../../shared/workflow/reassign-request-popup/reassign-request-popup.component';
import {RequestStatus, Task, TasksResponse} from '../../../../shared/workflow/workflow-v2.interface';
import {tasks_per_page, WorkflowV2Service} from '../../../../shared/workflow/workflow-v2.service';
import {CommunicationsService} from '../../../shared/communications.service';
import {TasksService} from '../../services/tasks.service';


@Component({
  selector: 'gp-tasks-in-progress-table',
  templateUrl: './tasks-in-progress-table.component.html',
  styleUrls: [
    '../../workflows-common.component.scss',
    '../../workflows.component.scss',
    './tasks-in-progress-table.component.scss'
  ]
})
export class TasksInProgressTableComponent extends Destroyable implements OnInit {

  public tasks: Task[] = [];
  public trigger$: Observable<number>;
  public loading = true;
  public pagination: Pagination = new DefaultPagination(tasks_per_page);
  public WORKFLOW_STATUS = [
    {name: 'WORKFLOWS.in_progress', key: RequestStatus.IN_PROGRESS},
    {name: 'WORKFLOWS.validated', key: RequestStatus.VALIDATED},
    {name: 'WORKFLOWS.rejected', key: RequestStatus.REJECTED},
    {name: 'WORKFLOWS.canceled', key: RequestStatus.CANCELED}
  ];
  public taskIdLastViewed: string;
  public userList: SelectEmployee[] = [];
  public selectedTaskToAssign: Task;
  public reassigning: boolean;
  public canReassign = false;
  public filterFormGroup: UntypedFormGroup = this.formBuilder.group({
    type: [''],
    subject: [''],
    status: [''],
    requester: ['']
  });
  public workflowTypes: { key: string }[];

  constructor(private activatedRoute: ActivatedRoute,
              private router: Router,
              private localization: LocalizationService,
              private workflowService: WorkflowV2Service,
              private toastrService: ToastrService,
              private taskService: TasksService,
              private translate: TranslateService,
              private modalService: ModalService,
              private roleService: RolesService,
              private formBuilder: UntypedFormBuilder,
              private communicationsServices: CommunicationsService) {
    super();
    this.trigger$ = merge(
      this.localization.changeDateFormatEvent,
      this.localization.changeLanguageEvent
    ).pipe(
      map(() => Math.random()),
      share(),
      takeUntil(this.componentDestroyed$)
    );

    this.workflowService.getWorkflowTypes().pipe(
      tap((types: string[]) => this.workflowTypes = types.map(type => ({key: type})))
    ).subscribe();

    this.communicationsServices.communicationChange
      .pipe(
        debounceTime(3000),
        switchMap(() => {
          this.pagination.currentPage.number = 0;
          return this.getTasks$();
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }

  public ngOnInit(): void {
    this.taskService.taskIdLastViewed$
      .pipe(
        tap((taskId: string) => {
          this.taskIdLastViewed = taskId;
          this.taskIdLastViewed ? this.filterFormGroup.disable() : this.filterFormGroup.enable();
        })
      ).subscribe();
    this.canReassign = this.roleService.hasRole('WORKFLOW_ADMIN', 'PEOPLE_WRITE', 'PEOPLE_READ', 'MSS_MY_TEAM');
    this.activatedRoute.queryParams
      .pipe(
        filter((queryParams: Params) => !!queryParams),
        tap((queryParams: Params) => {
          this.pagination.currentPage.number = queryParams.page || 0;
          if (filtersAreDifferent(this.filterFormGroup.value, queryParams)) {
            this.filterFormGroup.setValue({
              type: queryParams.type || '',
              requester: queryParams.requester || '',
              subject: queryParams.subject || '',
              status: queryParams.status || ''
            });
          }
        }),
        switchMap(() => this.getTasks$()),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();

    this.filterFormGroup.valueChanges
      .pipe(
        debounceTime(400),
        tap(filterValues => {
          if (!this.taskIdLastViewed) {
            this.updateRoute(filterValues);
          }
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();

  }

  public trackByTaskId(index: number, task: Task): string {
    return task.id;
  }

  public refreshTasks(): void {
    this.getTasks$().pipe(
      takeUntil(this.componentDestroyed$))
      .subscribe();
  }

  public resetFilters() {
    this.filterFormGroup.reset({type: '', requester: '', subject: '', status: ''});
  }

  public getTasks$() {
    this.loading = true;
    this.tasks = [];
    const search = buildFiltersQuery(this.filterFormGroup.value);
    return this.workflowService.getTasks({
      'per-page': this.pagination.perPage,
      page: this.pagination.currentPage.number,
      search
    })
      .pipe(
        catchError(() => of({data: [], total: 0})),
        tap((response: TasksResponse) => {
          this.tasks = response.data;
          this.pagination.update(response.total);
        }),
        finalize(() => this.loading = false)
      );
  }

  public viewTask(task: Task, event: MouseEvent): void {
    const targetElement = <HTMLElement>event.target;
    const isContextMenuButton = targetElement?.closest('button')?.classList?.contains('btn-context-menu');
    const isDropdownItem = targetElement?.closest('li')?.classList?.contains('dropdown-item');
    if (!isContextMenuButton && !isDropdownItem && task.status === RequestStatus.IN_PROGRESS) {
      task.read = true;
      this.taskIdLastViewed = task.id;
      this.router.navigate(['/inbox/tasks', task.id], {queryParamsHandling: 'merge'});
    }
  }

  public openReassignRequestModal(task: Task) {
    this.selectedTaskToAssign = task;
    this.modalService.openModalWithName('ReassignRequestPopupComponent').then((modalRef: ComponentRef<ReassignRequestPopupComponent>) => {
      modalRef.instance.reassigning = this.reassigning;
      modalRef.instance.reassign = this.reassignTask.bind(this);
    });
  }

  public reassignTask(modalRef: any, selectedUser: number ) {
    if (selectedUser != null) {
      this.reassigning = true;
      this.workflowService.reassignTask(this.selectedTaskToAssign.id, selectedUser.toString()).pipe(
        tap(() => {
          modalRef.close();
        }),
        catchError(() => {
          this.toastrService.error(this.translate.instant('WORKFLOWS.request_has_not_been_reassigned'));
          return EMPTY;
        }),
        finalize(() => {
          this.reassigning = false;
          this.selectedTaskToAssign.id = null;
          this.toastrService.success(this.translate.instant('WORKFLOWS.request_has_been_reassigned'));
        })
      ).subscribe();
    }
  }

  private updateRoute(filterValues: { [key: string]: string }): void {
    this.pagination.currentPage.number = 0;
    const queryParams = buildQueryParams(filterValues);
    this.router.navigate(['inbox/tasks'], {queryParams});
  }

  public closeSidePanel(isSidePanelOpen: boolean) {
    if (!isSidePanelOpen) {
      this.taskIdLastViewed = null;
      this.router.navigate(['inbox/tasks'], {queryParamsHandling: 'merge'});
    }
  }
}
